import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trophy, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, verifyOtp, resendOtp } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("password");
  const [code, setCode] = useState("");

  const finishLogin = (user) => {
    if (user.needs_onboarding) { navigate("/onboarding"); return; }
    toast.success(`Welcome back, ${user.name}`);
    navigate(user.role === "platform_admin" ? "/admin" : "/dashboard");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.mfa_required) {
        setStep("otp");
        toast.success(res.email_sent ? "We emailed you a 6-digit verification code." : "Enter your verification code.");
        return;
      }
      finishLogin(res);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const onVerify = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      finishLogin(await verifyOtp(email, code.trim()));
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const onResend = async () => {
    setErr("");
    try {
      await resendOtp(email);
      toast.success("A new code is on its way.");
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white text-[#0B0B12]">
      <div className="lg:w-1/2 flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 mb-12" data-testid="login-brand">
            <div className="w-8 h-8 bg-[#1E4FFF] flex items-center justify-center rounded-sm">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <span className="font-logo text-xl whitespace-nowrap text-[#0B0B12]">SALES<span className="text-[#1E4FFF]">ARENA HQ</span></span>
          </Link>
          <h1 className="font-display text-4xl lg:text-5xl mb-2 text-[#0B0B12]">WELCOME BACK.</h1>
          <p className="text-[#4B5563] mb-10">{step === "password" ? "Log in to your arena." : `Enter the 6-digit code we emailed to ${email}.`}</p>

          {step === "password" ? (
          <form onSubmit={onSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="font-label text-[#4B5563]">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-2 bg-white border-[#D5D9E2] h-12 rounded-sm text-[#0B0B12] placeholder:text-[#9AA1AD] focus-visible:ring-[#1E4FFF]"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="font-label text-[#4B5563]">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 bg-white border-[#D5D9E2] h-12 rounded-sm text-[#0B0B12] placeholder:text-[#9AA1AD] focus-visible:ring-[#1E4FFF]"
                data-testid="login-password-input"
              />
            </div>
            {err && (
              <div className="text-sm text-[#E13C32] bg-[#E13C32]/10 border border-[#E13C32]/30 p-3 rounded-sm" data-testid="login-error">
                {err}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1E4FFF] hover:bg-[#1740D6] text-white font-label h-12 rounded-sm"
              data-testid="login-submit-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log in →"}
            </Button>
          </form>
          ) : (
          <form onSubmit={onVerify} className="space-y-6">
            <div>
              <Label htmlFor="otp" className="font-label text-[#4B5563]">Verification code</Label>
              <Input
                id="otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                className="mt-2 bg-white border-[#D5D9E2] h-12 rounded-sm text-center tracking-[0.5em] text-lg text-[#0B0B12] placeholder:text-[#9AA1AD] focus-visible:ring-[#1E4FFF]"
                data-testid="login-otp-input"
              />
            </div>
            {err && (
              <div className="text-sm text-[#E13C32] bg-[#E13C32]/10 border border-[#E13C32]/30 p-3 rounded-sm" data-testid="login-otp-error">
                {err}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading || code.length < 6}
              className="w-full bg-[#1E4FFF] hover:bg-[#1740D6] text-white font-label h-12 rounded-sm"
              data-testid="login-otp-verify-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & log in →"}
            </Button>
            <div className="flex items-center justify-between text-sm">
              <button type="button" onClick={() => { setStep("password"); setCode(""); setErr(""); }} className="text-[#4B5563] hover:underline" data-testid="login-otp-back">
                ← Back
              </button>
              <button type="button" onClick={onResend} className="text-[#1E4FFF] hover:underline" data-testid="login-otp-resend">
                Resend code
              </button>
            </div>
          </form>
          )}

          <p className="mt-8 text-sm text-[#4B5563]">
            New to SalesArena HQ?{" "}
            <Link to="/signup" className="text-[#1E4FFF] hover:underline" data-testid="login-signup-link">
              Create your arena
            </Link>
          </p>
          <p className="mt-2 text-sm text-[#4B5563]">
            <Link to="/forgot-password" className="text-[#1E4FFF] hover:underline" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#121212]">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-50"
          style={{ backgroundImage: "url(https://images.pexels.com/photos/12060425/pexels-photo-12060425.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A]/60 to-[#0A0A0A]/30" />
        <div className="relative z-10 flex flex-col justify-end p-16 text-white">
          <div className="font-label text-[#34C759] mb-3">// TOP REP THIS WEEK</div>
          <div className="font-display text-7xl mb-2">MARCUS C.</div>
          <div className="font-mono text-2xl text-[#1E4FFF]">12,480 PTS · 23-DAY STREAK</div>
        </div>
      </div>
    </div>
  );
}
